import { APP_CONFIG_WIRE_KEYS } from "../constants/app-config-wire-keys";
import { parseAppConfig } from "../schema/app-config";
import type { IAppConfigResponse, TWireAppConfig } from "../types/app-config";
import { isRecord } from "./type-guards";

type TAppConfigGroupName = keyof typeof APP_CONFIG_WIRE_KEYS;

const isAppConfigGroupName = (name: string): name is TAppConfigGroupName =>
  Object.hasOwn(APP_CONFIG_WIRE_KEYS, name);

const GROUP_NAMES: readonly TAppConfigGroupName[] =
  Object.keys(APP_CONFIG_WIRE_KEYS).filter(isAppConfigGroupName);

// NOTE: NSAI-1606 — the readable field name ↔ wire key pairs of one group,
// read through Object.entries so the group's literal key union does not have
// to be spelled out for each of them.
const getFieldPairs = (
  groupName: TAppConfigGroupName,
): ReadonlyArray<readonly [string, string]> =>
  Object.entries(APP_CONFIG_WIRE_KEYS[groupName].fields);

// NOTE: NSAI-1606 — translates the obfuscated wire payload back to readable
// names, then validates it. Unknown keys are dropped, and whatever is missing
// or malformed takes its safe default from the schema, so this always returns
// a complete configuration.
export const fromWireFormat = (wire: unknown): IAppConfigResponse => {
  const wireRecord = isRecord(wire) ? wire : {};
  const readable: Record<string, Record<string, unknown>> = {};

  for (const groupName of GROUP_NAMES) {
    const wireGroup = wireRecord[APP_CONFIG_WIRE_KEYS[groupName].key];
    const groupRecord = isRecord(wireGroup) ? wireGroup : {};
    const readableGroup: Record<string, unknown> = {};

    for (const [fieldName, wireKey] of getFieldPairs(groupName)) {
      if (groupRecord[wireKey] !== undefined) {
        readableGroup[fieldName] = groupRecord[wireKey];
      }
    }

    readable[groupName] = readableGroup;
  }

  return parseAppConfig(readable);
};

// NOTE: NSAI-1606 — the inverse, for test fixtures and mock servers that need
// to serve what the real server would. The server has its own copy in
// server/src/app-config/to-wire-format.ts; both read the same key map.
export const toWireFormat = (readable: IAppConfigResponse): TWireAppConfig => {
  const wire: TWireAppConfig = {};

  for (const groupName of GROUP_NAMES) {
    const readableGroup: Record<string, unknown> = { ...readable[groupName] };
    const wireGroup: Record<string, unknown> = {};

    for (const [fieldName, wireKey] of getFieldPairs(groupName)) {
      if (readableGroup[fieldName] !== undefined) {
        wireGroup[wireKey] = readableGroup[fieldName];
      }
    }

    wire[APP_CONFIG_WIRE_KEYS[groupName].key] = wireGroup;
  }

  return wire;
};
